/**
 * Share links: the link that travels with a shared picture, and the page it
 * leads to.
 *
 * A URL printed on a photograph is not a link, so the picture carries only a
 * mark and the link goes where it can be clicked: the share text, the
 * clipboard, a pin's destination. Every share mints its own code, and every
 * visit to the page behind it is counted with where it came from.
 *
 * Where the links point (shareBaseUrl) is VITE_SHARE_BASE_URL, falling back
 * to this app's own address. Unset, nothing changes: links go to the app.
 * Set it to the backend's public address (https://seal-app-zys9t.ondigitalocean.app)
 * or to a subdomain pointed at the backend (https://share.gardengameplan.com)
 * and pasted links show a preview: the backend answers /s/:code, /g/:code
 * and a gardener's own garden address, /@:slug,
 * with the title, words and picture a crawler reads (routes/sharePreview),
 * and sends the person straight on to the same page here. Crawlers do not
 * run this app's script, so the app alone cannot give them those.
 *
 * A season review ("Your 2026 season") has links of its own, with its own
 * Public switch (getSeasonSharing, setSeasonPublic): minted with its year in
 * place of a plant or a location.
 *
 * Endpoints (every answer also carries `success`):
 * - POST   /shares { plantObjId | locationId | season, photoIds, layout,
 *   size, words?, code?, isPublic?, soFar? } → { code, path, wordsHeld? }.
 * - POST   /shares/:code/used { action, words } → { wordsHeld? } (quiet).
 * - POST   /shares/:code/frame/:index?count= (the image bytes) (quiet).
 * - GET    /shares/:code[?count=0&lang=] → PublicShare (no sign-in; the plant's names in the app's language).
 * - GET|PUT /shares/plant/:id, /shares/location/:id, /shares/season/:year →
 *   PlantSharing; PUT { isPublic, galleryName? }.
 *
 * Who uses it: features/plant-list (SharePlantDialog, PlantCard),
 * features/locations LocationCard, features/season SeasonReviewPage,
 * features/share (SharePage, PublicPageSwitch, gardenPage), the profile's
 * GardenPageRow, api/gardens (shareBaseUrl), and sign-up (LoginPage,
 * CompleteSignup: the referral kept in localStorage, heldReferral). Whether
 * a referral still credits a sign-up is the server's (welcome.REFERRAL_DAYS,
 * applied at POST /users to when the page was opened; audit A73). Caching:
 * [PLANT_SHARING_QUERY_KEY, id], [LOCATION_SHARING_QUERY_KEY, id] and
 * ['season-sharing', year] (held by useShareCount), SHARING_STALE_MS, invalidated when a switch changes. Auth: the
 * signed-in token for everything but the public page (getShare).
 *
 * @module shares
 */

import { apiClient } from './apiClient';
import { VITE_SHARE_BASE_URL } from '@/config/env-vars';
import type { Subject } from './subject';
import type { ShareLayout, ShareSize } from '@/features/plant-list/lib/shareFrames';
import type { LinkKey } from '@/shared/lib/socialLinks';
import { toast } from '@/shared/components/ui/use-toast';
import { getLanguage, t } from '@/shared/i18n/i18nSetup';
import { trackEvent } from '@/shared/lib/analytics';
import { isServerFailure, reportServerFailure } from '@/shared/lib/serverFailure';
import type { PlantNames } from '@/shared/types/plant';
import type { DateParts } from '@/shared/utils/dateUtils';
import {
  REFERRAL_AT_STORAGE_KEY,
  REFERRAL_CODE_STORAGE_KEY,
  REFERRAL_NAME_STORAGE_KEY,
  SEEN_SHARES_MAX,
  SEEN_SHARES_STORAGE_KEY,
} from '@/config/tunables/shared';

/** The key that follows a visitor from the page into signup. */
export const REFERRAL_KEY = REFERRAL_CODE_STORAGE_KEY;
/** The name of the gardener whose share brought them, when that plant is public. */
export const REFERRAL_NAME_KEY = REFERRAL_NAME_STORAGE_KEY;
/** When this phone opened the share page (milliseconds since the epoch), sent with sign-up so the server can tell an old one. */
export const REFERRAL_AT_KEY = REFERRAL_AT_STORAGE_KEY;

/** The share this browser keeps for a sign-up: its code, when its page was opened, and its gardener's name. */
export interface HeldReferral {
  /** The share's (or garden's) code. */
  code: string;
  /** When this phone opened its page, as an ISO time; null when it was kept with no date. */
  openedAt: string | null;
  /** The gardener's name while their plant is public, else null. */
  name: string | null;
}

/**
 * The share kept in this browser for a sign-up, as it was kept. Whether it
 * still credits one is not decided here: sign-up sends the code and when its
 * page was opened, and the server keeps it only within welcome.REFERRAL_DAYS
 * (audit A73; owner R19, 2026-10-18).
 *
 * @returns The share's code, when its page was opened and its gardener's name; null when none is kept.
 */
export function heldReferral(): HeldReferral | null {
  try {
    const code = window.localStorage.getItem(REFERRAL_KEY);
    if (!code) return null;
    const at = Number(window.localStorage.getItem(REFERRAL_AT_KEY));
    return {
      code,
      openedAt: Number.isFinite(at) && at > 0 ? new Date(at).toISOString() : null,
      name: window.localStorage.getItem(REFERRAL_NAME_KEY),
    };
  } catch {
    return null;
  }
}

/** A share link as saved: its code and its path on this app. */
export interface MintedShare {
  /** The link's eight-letter code. */
  code: string;
  /** Relative, e.g. /s/k7Q2abcd. */
  path: string;
}

/**
 * The letters a share's code is made of, as the backend makes them (no 0, O,
 * 1, l or I). The backend's contract, with the code's length of eight.
 */
const CODE_ALPHABET = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/**
 * A new share code, made here: eight letters the backend accepts as its own
 * (routes/shares). So the link can be shown and sent before anything is
 * saved, and saved at the first share, copy or save.
 *
 * Every letter is equally likely: a random byte at or above the largest
 * multiple of the alphabet's length (224 for 56 letters) is thrown away and
 * another drawn, since taking it modulo 56 favoured the first 32 letters.
 *
 * @returns Eight random letters from CODE_ALPHABET.
 */
export function newShareCode(): string {
  // Bytes from here up would wrap round the alphabet unevenly.
  const limit = 256 - (256 % CODE_ALPHABET.length);
  const bytes = new Uint8Array(16);
  let code = '';
  while (code.length < 8) {
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte < limit && code.length < 8) code += CODE_ALPHABET[byte % CODE_ALPHABET.length];
    }
  }
  return code;
}

/** What a link is laid out as: a plant's or bed's layout, or a season review's. */
export type LinkLayout = ShareLayout | 'season';

/**
 * Saves a share link: with the app's own code (newShareCode), or a new one
 * from the backend when none is given. The same code sent twice answers
 * the link already made.
 *
 * A season review's link sends season (its year) and layout 'season' in
 * place of a plant or a location, its photoIds the plant photos on its
 * picture (none is fine), isPublic for its switch while the season has
 * no link yet, and soFar while the season is still under way (its page then
 * says "So far, as of" the day the link was made).
 *
 * Words the server holds for a quick review (its public-words check) are
 * left off the link, and the gardener is told so.
 *
 * @param details - What is shared, and the code to save it under.
 * @returns The code and its path.
 */
export async function mintShare(details: {
  plantObjId?: number;
  locationId?: number;
  season?: number;
  /** A season's link made while the season runs ("Your season so far"). */
  soFar?: boolean;
  isPublic?: boolean;
  photoIds: number[];
  layout: LinkLayout;
  size: ShareSize;
  words?: string | null;
  code?: string;
}): Promise<MintedShare> {
  const response = await apiClient.post<{ success: boolean; code: string; path: string; wordsHeld?: boolean }>('/shares', details);
  if (response.data.wordsHeld) sayWordsHeld();
  return { code: response.data.code, path: response.data.path };
}

/**
 * Tells the gardener their words wait for a quick review: the server's
 * public-words check held them, and the link went out without them for now.
 */
function sayWordsHeld(): void {
  toast({ description: t('share.wordsHeld') });
}

/**
 * The address share and garden links start with: VITE_SHARE_BASE_URL when
 * the build was given one (the backend's preview pages), else this app's own.
 *
 * @returns An origin with no trailing slash, e.g. https://grow.gardengameplan.com.
 */
export function shareBaseUrl(): string {
  return (VITE_SHARE_BASE_URL.trim() || window.location.origin).replace(/\/+$/, '');
}

/**
 * The full address of a share, as it is sent (see shareBaseUrl).
 *
 * @param code - The share's code.
 */
export function shareUrlFor(code: string): string {
  return `${shareBaseUrl()}/s/${code}`;
}

/**
 * Which button the gardener pressed, once they have, and the words they
 * settled on. Quiet to the gardener on failure (the share has gone ahead);
 * a failure on our side is sent to Sentry (D31; bug B1).
 *
 * @param code - The share's code.
 * @param action - The button.
 * @param words - The words posted with it, or null.
 */
export async function markShareUsed(
  code: string, action: 'share' | 'save' | 'copy', words: string | null = null
): Promise<void> {
  try {
    const response = await apiClient.post<{ success: boolean; wordsHeld?: boolean }>(`/shares/${code}/used`, { action, words });
    trackEvent('share', { action });
    if (response.data?.wordsHeld) sayWordsHeld();
  } catch (error) {
    // A miss here loses one number; it must not stop a share, but we hear of it.
    if (isServerFailure(error)) reportServerFailure(error, 'share.used');
  }
}

/**
 * Keeps the pictures that were posted with a link, in order, so the page
 * behind it shows what the gardener sent. Quiet on failure: the share has
 * already happened; a failure on our side is sent to Sentry (D31; bug B1).
 *
 * @param code - The share's code.
 * @param frames - The pictures, in the order they were posted.
 */
export async function uploadShareFrames(code: string, frames: Blob[]): Promise<void> {
  try {
    for (let index = 0; index < frames.length; index += 1) {
      const frame = frames[index];
      await apiClient.post(`/shares/${code}/frame/${index}?count=${frames.length}`, frame, {
        headers: { 'Content-Type': frame.type || 'image/jpeg' },
      });
    }
  } catch (error) {
    // Without them the page falls back to the photographs; nothing for the gardener to do, but we hear of it.
    if (isServerFailure(error)) reportServerFailure(error, 'share.frames');
  }
}

/** What a share's public page shows. */
export interface PublicShare {
  /** The link's code. */
  code: string;
  /** True once the gardener has taken the link down. */
  revoked: boolean;
  /** True while the plant's public switch is on: the page may show the gardener's name. */
  open: boolean;
  /**
   * The plant this is a share of, or null for a location's: its kind (name,
   * never shown beside its name), its one name whole (variety: "Devil's Ear
   * Lettuce", services/grow/plantNaming) and its names.
   */
  plant: { name: string; variety: string; names: PlantNames } | null;
  /** The location this is a share of: its name, its description while open, and the public plants growing there, each by its one name (variety). */
  location: { name: string; description: string | null; plants: Array<{ name: string; variety: string; names: PlantNames; code: string | null }> } | null;
  /**
   * A season review's link: which season, and for one made while the season
   * ran, soFar and the gardener's day it was made (asOf, "YYYY-MM-DD"). Null
   * for a plant's or a bed's.
   */
  season: { year: number; soFar: boolean; asOf: string | null } | null;
  /** How the pictures were laid out ('season' for a season review). */
  layout: LinkLayout;
  /** The pictures' size. */
  size: ShareSize;
  /** When the link was made. */
  createdAt: string;
  /** The gardener's line, while the link stands. */
  words: string | null;
  /** The gardener, while the plant is public, with their garden page's code and readable address (slug, without its "@"). */
  gardener: { name: string; place: LinkKey | null; garden: string | null; slug: string | null } | null;
  /**
   * The pictures' photographs, for a link made before the pictures were kept:
   * url the card copy, large the 1600px copy for a wide screen (D53; absent
   * for a bed's cover).
   */
  photos: Array<{ id: number; dateTaken: string | null; stage: string | null; url: string; large?: string }>;
  /** The first picture posted with this link. */
  frame: string | null;
  /** Every picture posted with this link, in the order the sheet made them. */
  frames: string[];
  /** Which parts of a date the gardener's pages show (Profile > Public pages). */
  dates: DateParts;
}

/** Where this browser keeps the share codes it has opened. */
const SEEN_KEY = SEEN_SHARES_STORAGE_KEY;

/**
 * Whether this browser has opened this link before, noting it if not.
 *
 * A visit is one person opening a link, not every load of the page: a
 * refresh, a second look, the development build running every effect twice,
 * all used to add to the count.
 *
 * @param code - The share's code.
 * @returns True the first time; also true when storage is refused, so the visit counts.
 */
function firstVisit(code: string): boolean {
  try {
    const seen: string[] = JSON.parse(window.localStorage.getItem(SEEN_KEY) || '[]');
    if (seen.includes(code)) return false;
    window.localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, code].slice(-SEEN_SHARES_MAX)));
    return true;
  } catch {
    return true;
  }
}

/**
 * The page behind a code. Needs no account; counts the visit once per browser.
 *
 * @param code - The share's code, from the address (/s/:code); encoded, since
 *   it is whatever was typed or pasted there.
 */
export async function getShare(code: string): Promise<PublicShare> {
  const count = firstVisit(code);
  // The app's language, so the plant's names come in it (services/grow/plantNaming).
  const response = await apiClient.get<PublicShare & { success: boolean }>(`/shares/${encodeURIComponent(code)}`, { params: { ...(count ? {} : { count: 0 }), lang: getLanguage() } });
  return response.data;
}

/** A plant's, a location's or a season's Public switch and its numbers. */
export interface PlantSharing {
  /** Whether the switch is on: its pages show the gardener's chosen name. */
  isPublic: boolean;
  /** How many links have been made for it. */
  shares: number;
  /** How many visits those links have had, one per browser. */
  visits: number;
  /** The server's call: the switch must ask for the first consent before it turns on (A41). */
  needsConsent: boolean;
  /** The server's call: a plant's switch must also ask for the name to show in the gallery. */
  needsGalleryName: boolean;
}

/**
 * The plant's switch and its numbers.
 *
 * @param plantObjId - The plant.
 */
export async function getPlantSharing(plantObjId: number): Promise<PlantSharing> {
  const response = await apiClient.get<PlantSharing & { success: boolean }>(`/shares/plant/${plantObjId}`);
  return response.data;
}

/**
 * The plant's public switch. Turning it on is the consent, and offers the
 * plant's shared photos to the website gallery; off takes them back.
 *
 * @param plantObjId - The plant.
 * @param isPublic - On or off.
 * @param galleryName - The name to show under gallery photos, given with the first consent.
 */
export async function setPlantPublic(plantObjId: number, isPublic: boolean, galleryName?: string): Promise<void> {
  await apiClient.put(`/shares/plant/${plantObjId}`, { isPublic, ...(galleryName ? { galleryName } : {}) });
}

/**
 * A subject's switch and numbers: a plant's, or a location's through /shares/location.
 *
 * @param subject - The plant or location.
 */
export async function getSubjectSharing(subject: Subject): Promise<PlantSharing> {
  if (subject.kind === 'plant') return getPlantSharing(subject.id);
  const response = await apiClient.get<PlantSharing & { success: boolean }>(`/shares/location/${subject.id}`);
  return response.data;
}

/**
 * Turns a plant's or a location's Public switch on or off.
 *
 * @param subject - The plant or location.
 * @param isPublic - On or off.
 * @param galleryName - A plant's first consent: the name to show in the gallery.
 */
export async function setSubjectPublic(subject: Subject, isPublic: boolean, galleryName?: string): Promise<void> {
  if (subject.kind === 'plant') return setPlantPublic(subject.id, isPublic, galleryName);
  await apiClient.put(`/shares/location/${subject.id}`, { isPublic });
}

/**
 * A season review's Public switch and its numbers: off, with zeros, before
 * the season has a link.
 *
 * @param year - The season's year.
 */
export async function getSeasonSharing(year: number): Promise<PlantSharing> {
  const response = await apiClient.get<PlantSharing & { success: boolean }>(`/shares/season/${year}`);
  return response.data;
}

/**
 * Turns a season review's Public switch on or off, for every link of that
 * season. With no link yet nothing is stored; the switch goes with the
 * first link (mintShare's isPublic).
 *
 * @param year - The season's year.
 * @param isPublic - On or off.
 */
export async function setSeasonPublic(year: number, isPublic: boolean): Promise<void> {
  await apiClient.put(`/shares/season/${year}`, { isPublic });
}
